import React, { lazy, Suspense, useState, useEffect } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "./App.css";
import "react-toastify/dist/ReactToastify.css";
import "react-datepicker/dist/react-datepicker.css";

// Eagerly load critical components for faster initial render
import ProtectedRoute from "./components/common/ProtectedRoute";
import LogoIcon from "./components/common/LogoIcon";
// MainScreen is eagerly loaded as it's the app shell - lazy loading happens inside it
import MainScreen from "./screens/MainScreen";

// Code-split auth route components - only load when needed
const LoginScreen = lazy(() => import("./screens/auth-screens/LoginScreen"));
const RegisterCompanyScreen = lazy(
  () => import("./screens/auth-screens/RegisterCompanyScreen"),
);
const RegisterDriverScreen = lazy(
  () => import("./screens/auth-screens/RegisterDriverScreen"),
);
const Logout = lazy(() => import("./components/Logout"));
const ResetPassword = lazy(() => import("./screens/ResetPassword"));
const AcceptInvitation = lazy(() => import("./screens/AcceptInvitation"));
const EmailVerification = lazy(() => import("./screens/EmailVerification"));
const TermsOfUse = lazy(() => import("./screens/TermsOfUse"));
const PrivacyPolicy = lazy(() => import("./screens/PrivaryPolicy"));

// Delayed fallback to prevent flicker (rerender-usetransition-loading)
// Only shows loading state if it takes longer than 200ms
const DelayedFallback = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 200);
    return () => clearTimeout(timer);
  }, []);

  if (!show) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "80vh",
        backgroundColor: "#fff",
      }}
    >
      <LogoIcon />
    </div>
  );
};

// Wrapper to add Suspense boundary per route (async-suspense-boundaries)
const withSuspense = (Component) => (props) => (
  <Suspense fallback={<DelayedFallback />}>
    <Component {...props} />
  </Suspense>
);

function App() {
  return (
    <>
      <ToastContainer />
      <Switch>
        <Route path="/verify" render={withSuspense(EmailVerification)} />
        <Route path="/pwreset" render={withSuspense(ResetPassword)} />
        <Route path="/login" render={withSuspense(LoginScreen)} />
        <Route path="/logout" render={withSuspense(Logout)} />
        <Route
          path="/register_company"
          render={withSuspense(RegisterCompanyScreen)}
        />
        <Route
          path="/register_driver"
          render={withSuspense(RegisterDriverScreen)}
        />
        <Route path="/accept_company" render={withSuspense(AcceptInvitation)} />
        <Route path="/terms" render={withSuspense(TermsOfUse)} />
        <Route path="/policy" render={withSuspense(PrivacyPolicy)} />
        <ProtectedRoute path="/app" component={MainScreen} />
        <Redirect from="/" to="/login" />
      </Switch>
    </>
  );
}

export default App;
